/**
 * 訂單異常 Exceptions lens (S4.3) — the worklist.
 *
 * Rows are RE-DERIVED per read by the line×rule engine (S4.2): resolving the
 * source fact auto-clears them — there is NO manual close anywhere. Sorted
 * severity-then-age (shared `sortExceptionWorklist`). Segmented category
 * chips carry counts; each row gets a one-line diagnosis + 建議動作; the 80px
 * 「鑽取來源」action drills to the OWNING module route — write-back happens
 * THERE, OCC never edits. Acknowledge / snooze-with-reason go through
 * `ReasonRequiredModal` and are client-side coordination marks; snoozes
 * EXPIRE and the row resurfaces.
 */

import { useEffect, useMemo, useState } from 'react';
import { App, Button, Checkbox, Dropdown, Segmented, Select, Table, Tag, Typography, theme } from 'antd';
import type { TableProps } from 'antd';
import {
  CheckOutlined,
  ClockCircleOutlined,
  ExportOutlined,
  MoreOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import TableContainer, { ListEmptyState, renderDash } from '@/components/common/TableContainer';
import ReasonRequiredModal from '@/components/common/ReasonRequiredModal';
import { useOccDocIndex, useOccExceptions, useOccLines } from '@/hooks/useOcc';
import { useFeature } from '@/features/flags';
import { useOccStore } from '@/stores/occStore';
import type { OccReasonFilter } from '@/stores/occStore';
import type { OccExceptionReason, OccExceptionRow } from '@/lib/occ/selectors';
import { useFormStyles } from '@/styles/formStyles';
import {
  SNOOZE_DURATION_HOURS,
  drillRouteForException,
  filterExceptionRows,
  isSnoozeActive,
  snoozeUntil,
} from '../occWorkbench';
import { LensFooter, LensUnavailableNotice, SeverityTag } from '../lensShared';

const { Text } = Typography;

const REASONS: OccExceptionReason[] = [
  'back_order',
  'short_load',
  'overflow',
  'delay',
  'price_review',
];

type MarkMode = 'acknowledge' | 'snooze';

const ExceptionsLens = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['occ', 'procurement', 'common']);
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const formStyles = useFormStyles();

  // SR4.1 cross-link — a shortage (欠交) exception row offers a jump into the
  // 補貨建議 review-queue lens, product-filtered. Gated on the SAME flag as the
  // lens itself (`reorder.suggestions`): flag-off adds ZERO new UI here, so this
  // lens stays byte-identical to base (the reversibility guard, Fixed dec. 21).
  const reorderEnabled = useFeature('reorder.suggestions');

  const [searchParams, setSearchParams] = useSearchParams();
  // Route-derived `?productId=` scope (SR2.2) — same canonical internalProductId
  // deep link the OCC lines lens honours (SR3.5's 產品管控中心 quintet).
  const productFilter = searchParams.get('productId');

  const { rows, summary, syncedAt, isLoading, error, refresh, unavailable } = useOccExceptions();
  const { lines } = useOccLines();
  const docIndex = useOccDocIndex();

  const clearProductFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('productId');
    setSearchParams(next, { replace: true });
  };
  const {
    exceptionFilters,
    setExceptionFilters,
    snoozes,
    acks,
    snoozeException,
    acknowledgeException,
    pruneExceptionMarks,
  } = useOccStore();

  const [markTarget, setMarkTarget] = useState<{ mode: MarkMode; row: OccExceptionRow } | null>(
    null
  );
  const [snoozeHours, setSnoozeHours] = useState<number>(48);

  // Rows auto-clear server-side; drop marks whose row no longer exists.
  useEffect(() => {
    if (!isLoading && rows.length >= 0) {
      pruneExceptionMarks(rows.map((row) => row.id));
    }
    // pruning is idempotent over the same row set
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, isLoading]);

  const nowIso = syncedAt ?? new Date().toISOString();

  const visibleRows = useMemo(
    () =>
      filterExceptionRows(
        rows,
        exceptionFilters.reason,
        exceptionFilters.showSnoozed,
        snoozes,
        nowIso,
        productFilter
      ),
    [rows, exceptionFilters, snoozes, nowIso, productFilter]
  );

  // Chip label — the scoped product's SKU (exception rows carry it), else the
  // raw internalProductId when the deep link outlives the last flagged row.
  const productChipLabel = useMemo(
    () =>
      rows.find((row) => row.internalProductId === productFilter)?.sku ??
      lines.find((line) => line.internalProductId === productFilter)?.sku ??
      productFilter,
    [rows, lines, productFilter]
  );

  const drill = (row: OccExceptionRow) => {
    const line = lines.find((candidate) => candidate.id === row.lineId);
    const route = drillRouteForException(
      row,
      line?.poLineIds ?? [],
      (poLineId) => docIndex.poForPoLine(poLineId)?.poId
    );
    navigate(route);
  };

  const submitMark = (reason: string) => {
    if (!markTarget) return;
    const at = new Date().toISOString();
    if (markTarget.mode === 'acknowledge') {
      acknowledgeException(markTarget.row.id, reason, at);
      message.success(t('occ:occ.exceptions.messages.acknowledged'));
    } else {
      snoozeException(markTarget.row.id, reason, snoozeUntil(at, snoozeHours), at);
      message.success(t('occ:occ.exceptions.messages.snoozed'));
    }
    setMarkTarget(null);
  };

  // F2.3 absent-≠-zero: when the summary is ABSENT (degraded seam) the chip
  // counts are HIDDEN — never rendered as a false `0` claim. Mock mode always
  // carries a summary, so its chips are byte-identical to before.
  const chipCount = (count: number | undefined): string =>
    count == null ? '' : ` ${count}`;

  const segmentedOptions = [
    {
      value: 'all',
      label: `${t('occ:occ.exceptions.chipAll')}${chipCount(summary?.total)}`,
    },
    ...REASONS.map((reason) => ({
      value: reason,
      label: `${t(`occ:occ.exceptions.reason.${reason}`)}${chipCount(summary?.byReason[reason])}`,
    })),
  ];

  const columns: TableProps<OccExceptionRow>['columns'] = [
    {
      title: t('occ:occ.exceptions.columns.severity'),
      key: 'severity',
      width: 90,
      render: (_: unknown, row) => <SeverityTag severity={row.severity} />,
    },
    {
      title: t('occ:occ.exceptions.columns.category'),
      key: 'reason',
      width: 120,
      render: (_: unknown, row) => (
        <Tag style={{ margin: 0 }}>{t(`occ:occ.exceptions.reason.${row.reason}`)}</Tag>
      ),
    },
    {
      title: t('occ:occ.exceptions.columns.order'),
      key: 'order',
      width: 200,
      render: (_: unknown, row) => (
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          <span style={{ fontWeight: token.fontWeightStrong }}>{row.soNumber}</span>
          <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
            {row.sku} · {row.customerName}
          </span>
        </div>
      ),
    },
    {
      title: t('occ:occ.exceptions.columns.diagnosis'),
      key: 'diagnosis',
      width: 280,
      render: (_: unknown, row) => (
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          <span>{t(`occ:occ.exceptions.diagnosis.${row.reason}`, { qty: row.qtyImpact })}</span>
          <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
            {t(`occ:occ.exceptions.suggestion.${row.reason}`)}
          </Text>
        </div>
      ),
    },
    {
      title: t('occ:occ.exceptions.columns.aging'),
      key: 'aging',
      width: 130,
      render: (_: unknown, row) => (
        <div className="tw-flex tw-items-center tw-gap-2">
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {t('occ:occ.exceptions.aging.daysValue', { days: row.agingDays })}
          </span>
          <Tag style={{ margin: 0 }}>
            {t('occ:occ.exceptions.aging.bucketValue', { bucket: row.agingBucket })}
          </Tag>
        </div>
      ),
    },
    {
      title: t('occ:occ.exceptions.columns.owner'),
      key: 'owner',
      width: 90,
      render: (_: unknown, row) => t(`occ:occ.owner.${row.owner}`),
    },
    {
      title: t('occ:occ.exceptions.columns.status'),
      key: 'status',
      width: 170,
      render: (_: unknown, row) => {
        const snooze = snoozes[row.id];
        const snoozed = isSnoozeActive(snooze, nowIso);
        const acked = Boolean(acks[row.id]);
        if (!snoozed && !acked) return renderDash();
        return (
          <div className="tw-flex tw-flex-col tw-gap-1">
            {acked && (
              <Tag color="blue" style={{ margin: 0 }}>
                {t('occ:occ.exceptions.marks.acknowledged')}
              </Tag>
            )}
            {snoozed && snooze && (
              <Tag style={{ margin: 0 }}>
                {t('occ:occ.exceptions.marks.snoozedUntil', {
                  time: dayjs(snooze.until).format('MM-DD HH:mm'),
                })}
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      title: t('occ:occ.exceptions.columns.actions'),
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_: unknown, row) => {
        const snoozed = isSnoozeActive(snoozes[row.id], nowIso);
        const acked = Boolean(acks[row.id]);
        return (
          <Dropdown
            menu={{
              items: [
                {
                  key: 'drill',
                  label: t('occ:occ.exceptions.actions.drill'),
                  icon: <ExportOutlined />,
                  onClick: (e) => {
                    e.domEvent.stopPropagation();
                    drill(row);
                  },
                },
                // SR4.1 cross-link — only for shortage (欠交) rows, and only when
                // the reorder queue is enabled (hidden otherwise, house tenet):
                // jump to the 補貨建議 lens filtered to this product.
                ...(reorderEnabled && row.reason === 'back_order'
                  ? [
                      {
                        key: 'reorder',
                        label: t('procurement:demandPool.reorder.lensLabel'),
                        icon: <ShoppingCartOutlined />,
                        onClick: (e: { domEvent: { stopPropagation: () => void } }) => {
                          e.domEvent.stopPropagation();
                          navigate(
                            `/auth/purchase-orders/demand-pool/reorder?productId=${encodeURIComponent(
                              row.internalProductId
                            )}`
                          );
                        },
                      },
                    ]
                  : []),
                // Already-marked actions are HIDDEN, not disabled (house tenet).
                ...(!acked
                  ? [
                      {
                        key: 'acknowledge',
                        label: t('occ:occ.exceptions.actions.acknowledge'),
                        icon: <CheckOutlined />,
                        onClick: (e: { domEvent: { stopPropagation: () => void } }) => {
                          e.domEvent.stopPropagation();
                          setMarkTarget({ mode: 'acknowledge', row });
                        },
                      },
                    ]
                  : []),
                ...(!snoozed
                  ? [
                      {
                        key: 'snooze',
                        label: t('occ:occ.exceptions.actions.snooze'),
                        icon: <ClockCircleOutlined />,
                        onClick: (e: { domEvent: { stopPropagation: () => void } }) => {
                          e.domEvent.stopPropagation();
                          setSnoozeHours(48);
                          setMarkTarget({ mode: 'snooze', row });
                        },
                      },
                    ]
                  : []),
              ],
            }}
            trigger={['click']}
          >
            <Button
              type="text"
              aria-label={t('occ:occ.exceptions.actions.actionsFor', { ref: row.soNumber })}
              icon={<MoreOutlined />}
              onClick={(e) => e.stopPropagation()}
            />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <>
      <LensUnavailableNotice active={unavailable} domain="exceptions" />
      {/* Segmented category chips with counts + snoozed visibility toggle */}
      <div className="tw-mb-6 tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
        <Segmented
          value={exceptionFilters.reason}
          onChange={(value) => setExceptionFilters({ reason: value as OccReasonFilter })}
          options={segmentedOptions}
          size="large"
        />
        {productFilter && (
          <Tag
            closable
            onClose={clearProductFilter}
            style={{ marginInlineEnd: 0, paddingBlock: token.paddingXXS }}
          >
            {t('occ:occ.lines.columns.product')}: {productChipLabel}
          </Tag>
        )}
        <Checkbox
          checked={exceptionFilters.showSnoozed}
          onChange={(e) => setExceptionFilters({ showSnoozed: e.target.checked })}
          style={{ marginLeft: 'auto' }}
        >
          {t('occ:occ.exceptions.showSnoozed')}
        </Checkbox>
      </div>

      <TableContainer
        errorState={{
          active: Boolean(error),
          title: t('occ:occ.states.loadFailed'),
          retryLabel: t('common:errors.boundary.retry'),
          onRetry: refresh,
        }}
      >
        <Table<OccExceptionRow>
          rowKey="id"
          dataSource={visibleRows}
          columns={columns}
          loading={isLoading}
          onRow={(row) => ({
            onClick: () => drill(row),
            style: { cursor: 'pointer' },
          })}
          locale={{ emptyText: <ListEmptyState title={t('occ:occ.states.noExceptions')} /> }}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          scroll={{ x: 1160 }}
        />
      </TableContainer>
      <LensFooter syncedAt={syncedAt} />

      <ReasonRequiredModal
        open={Boolean(markTarget)}
        title={
          markTarget?.mode === 'snooze'
            ? t('occ:occ.exceptions.snoozeModal.title')
            : t('occ:occ.exceptions.ackModal.title')
        }
        submitText={
          markTarget?.mode === 'snooze'
            ? t('occ:occ.exceptions.snoozeModal.submit')
            : t('occ:occ.exceptions.ackModal.submit')
        }
        cancelText={t('common:actions.cancel')}
        reasonLabel={
          markTarget?.mode === 'snooze'
            ? t('occ:occ.exceptions.snoozeModal.reasonLabel')
            : t('occ:occ.exceptions.ackModal.reasonLabel')
        }
        requiredMessage={
          markTarget?.mode === 'snooze'
            ? t('occ:occ.exceptions.snoozeModal.reasonRequired')
            : t('occ:occ.exceptions.ackModal.reasonRequired')
        }
        reasonPlaceholder={
          markTarget?.mode === 'snooze'
            ? t('occ:occ.exceptions.snoozeModal.placeholder')
            : t('occ:occ.exceptions.ackModal.placeholder')
        }
        description={
          markTarget?.mode === 'snooze'
            ? t('occ:occ.exceptions.snoozeModal.description')
            : t('occ:occ.exceptions.ackModal.description')
        }
        onSubmit={submitMark}
        onCancel={() => setMarkTarget(null)}
      >
        {markTarget?.mode === 'snooze' && (
          <div style={{ marginBottom: token.marginMD }}>
            <Text style={formStyles.labelStyle}>
              {t('occ:occ.exceptions.snoozeModal.durationLabel')}
            </Text>
            <Select
              size="large"
              value={snoozeHours}
              onChange={(value) => setSnoozeHours(value)}
              style={{ ...formStyles.inputStyle, width: '100%' }}
              options={SNOOZE_DURATION_HOURS.map((hours) => ({
                value: hours,
                label: t(`occ:occ.exceptions.snoozeModal.durations.h${hours}`),
              }))}
            />
          </div>
        )}
      </ReasonRequiredModal>
    </>
  );
};

export default ExceptionsLens;
